/**
 * SupplierLeadTimesCard — three nullable int fields (standard / sample /
 * urgent) on the existing supplier record. Saves through the same
 * `updateSupplier` mutation the rest of the supplier form uses.
 */
import { useState, useEffect } from 'react';
import { Loader2, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import { useSupplierMutations } from '@/features/suppliers/hooks/useSupplierMutations';

interface Props {
  supplierId: string;
  initial: {
    standard: number | null;
    sample: number | null;
    urgent: number | null;
  };
  canEdit: boolean;
}

const toInput = (v: number | null) => (v == null ? '' : String(v));
const fromInput = (v: string): number | null => {
  const trimmed = v.trim();
  if (!trimmed) return null;
  const parsed = Number.parseInt(trimmed, 10);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return parsed;
};

export function SupplierLeadTimesCard({ supplierId, initial, canEdit }: Props) {
  const { updateSupplier } = useSupplierMutations();
  const [standard, setStandard] = useState(toInput(initial.standard));
  const [sample, setSample] = useState(toInput(initial.sample));
  const [urgent, setUrgent] = useState(toInput(initial.urgent));

  useEffect(() => {
    setStandard(toInput(initial.standard));
    setSample(toInput(initial.sample));
    setUrgent(toInput(initial.urgent));
  }, [initial.standard, initial.sample, initial.urgent]);

  const dirty =
    fromInput(standard) !== initial.standard ||
    fromInput(sample) !== initial.sample ||
    fromInput(urgent) !== initial.urgent;

  const handleSave = async () => {
    try {
      await updateSupplier.mutateAsync({
        supplierId,
        updates: {
          lead_time_standard_days: fromInput(standard),
          lead_time_sample_days: fromInput(sample),
          lead_time_urgent_days: fromInput(urgent),
        },
      });
      toast({ title: 'Lead times saved' });
    } catch (err) {
      toast({
        title: 'Failed to save lead times',
        description: err instanceof Error ? err.message : 'Try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Lead times (days)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label htmlFor={`${supplierId}-lt-std`} className="text-xs">Standard</Label>
            <Input
              id={`${supplierId}-lt-std`}
              type="number"
              min={0}
              value={standard}
              onChange={(e) => setStandard(e.target.value)}
              disabled={!canEdit}
              className="h-8 text-sm"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor={`${supplierId}-lt-sample`} className="text-xs">Sample</Label>
            <Input
              id={`${supplierId}-lt-sample`}
              type="number"
              min={0}
              value={sample}
              onChange={(e) => setSample(e.target.value)}
              disabled={!canEdit}
              className="h-8 text-sm"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor={`${supplierId}-lt-urgent`} className="text-xs">Urgent</Label>
            <Input
              id={`${supplierId}-lt-urgent`}
              type="number"
              min={0}
              value={urgent}
              onChange={(e) => setUrgent(e.target.value)}
              disabled={!canEdit}
              className="h-8 text-sm"
            />
          </div>
        </div>
        {canEdit && (
          <div className="flex justify-end">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleSave}
              disabled={!dirty || updateSupplier.isPending}
              className="h-8 px-3 text-xs"
            >
              {updateSupplier.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <>
                  <Save className="h-3.5 w-3.5 mr-1" /> Save
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
